import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { EppService } from './epp.service';

import { GetLegendPipe } from '../pipes/get-legend.pipe';
import { HasLegendPipe } from '../pipes/has-legend.pipe';
import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { HourPipe } from '../shared/hour.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { RrPipe } from '../shared/rr.pipe';
import { SunrisePipe } from '../shared/sunrise.pipe';
import { VisPipe } from '../shared/vis.pipe';
import { WindrosePipe } from '../shared/windrose.pipe';
import { ColorMappingPipe } from './pipes/color-mapping.pipe';
import { ColorMappingSnowPipe } from './pipes/color-mapping-snow.pipe';
import { IsHiddenParameterPipe } from './pipes/is-hidden-parameter.pipe';
import { IsNightPipe } from './pipes/is-night.pipe';
import { ParameterClassPipe } from './pipes/parameter-class.pipe';
import { ParameterColorPipe } from './pipes/paremeter-color.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  isHiddenParameter,
  isNight,
  isParameterAvailableForResolution,
} from './constants/constants';
import { TippyDirective } from '@ngneat/helipopper';
import { map } from 'rxjs';

export const MODULES = [
  CommonModule,
  MatTableModule,
  MatTooltipModule,
  MatIconModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  DatexPipe,
  HourPipe,
  WindrosePipe,
  RoundPipe,
  SunrisePipe,
  RrPipe,
  VisPipe,
  IsNilPipe,
  HasLegendPipe,
  GetLegendPipe,
  IsNightPipe,
  IsHiddenParameterPipe,
  ColorMappingPipe,
  ParameterColorPipe,
  ColorMappingSnowPipe,
  ParameterClassPipe,
];
export const DIRECTIVES = [TippyDirective, WmIconDirective];

@Component({
  selector: 'app-epp-table',
  templateUrl: './epp-table.component.html',
  styleUrls: ['./epp-table.component.scss', './epp-color-mapping.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    CdkDropList,
    CdkDrag,
  ],
})
export class EppTableComponent implements OnInit {
  private injector = inject(Injector);
  private platform = inject(Platform);
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  public eppService = inject(EppService);
  public auth = inject(AuthService);
  public languageService = inject(LanguageService);

  // Angular CDKs StickyStyler beobachtet Größenänderungen (u.a. beim Wechsel
  // in den Druck-Media-Query) und schreibt "position: sticky" per Inline-
  // Style neu – ein nachträgliches CSS-!important-Override von außen
  // (versucht in operational-report.edit.component.scss/.ts) verliert
  // diesen Wettlauf zuverlässig, da eine erneute Inline-Style-Zuweisung
  // (auch ohne !important) den vorherigen Wert samt !important-Flag
  // komplett ersetzt. Sticky Header/Spalten deshalb hier direkt über
  // Angular deaktivieren, statt hinterher dagegen anzukämpfen – siehe
  // isPrinting()-Verwendung in epp-table.component.html.
  isPrinting = signal(false);

  tableData = input<any>();
  parameterChanged = input<number>();
  parameterset = input<string>();
  userParameters = input<any>();
  parameterDefinitions = input<any>();
  resolution = input<any>();
  dashboardView = input<boolean>(false);
  stickyHeader = input<boolean>(true);
  highlightedHour = input<number>();
  headerContextMenu = output<string>();
  columnOrderChanged = output<string[]>();

  parameters = signal<any>(undefined);
  detailParameters = {};
  displayedColumns;
  displayedUnits;
  units = signal<any>(undefined);

  private handset = toSignal(
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(map(r => r.matches)),
    { initialValue: this.breakpointObserver.isMatched(['(max-width: 950px)']) }
  );

  readonly isMobilePlatform = computed(
    () => this.handset() || this.platform.ANDROID || this.platform.IOS
  );

  constructor() {
    this.listenToInputChanges();

    const onBeforePrint = () => this.isPrinting.set(true);
    const onAfterPrint = () => this.isPrinting.set(false);
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    });
  }

  ngOnInit(): void {
    this.parameters.set([...this.extractParameters()]);
    this.setColumns();
  }

  setColumns(): void {
    if (this.resolution().hourly < 12) {
      this.displayedColumns = ['dtg', ...this.parameters()];
    } else {
      this.displayedColumns = ['dtg', 'dayNight', ...this.parameters()];
    }
    this.setUnits();
  }

  onHeaderDrop(event: CdkDragDrop<string[]>): void {
    const fixedColumns =
      this.resolution().hourly < 12 ? ['dtg'] : ['dtg', 'dayNight'];

    const currentParameters = [...this.parameters()];
    moveItemInArray(currentParameters, event.previousIndex, event.currentIndex);

    this.parameters.set(currentParameters);
    this.setColumns();
    this.columnOrderChanged.emit([...currentParameters]);
  }

  extractParameters(): Array<string> {
    const parameters = [];
    const userParameters = this.userParameters();
    if (userParameters) {
      for (const parameter of userParameters) {
        if (!this.dashboardView() || parameters.length < 12) {
          if (this.isParameterAvailableForResolution(parameter.parameter)) {
            if (parameter.selected) {
              parameters.push(parameter.parameter);
            }
          }
        }
      }
    }
    return parameters;
  }

  setUnits(): void {
    this.displayedUnits = ['UNITS.dtg'];
    this.units.set(['dtg']);
    if (this.resolution().hourly === 12) {
      this.displayedUnits.push('UNITS.dayNight');
      this.units.update(units => {
        return [...units, 'dayNight'];
      });
    }
    for (const parameter of this.parameters()) {
      this.displayedUnits.push('UNITS.' + parameter);
      this.units.update(units => {
        return [...units, parameter];
      });
    }
  }

  onHeaderContextMenu(event: MouseEvent, parameter: string): void {
    event.preventDefault();
    this.headerContextMenu.emit(parameter);
  }

  isGroup(index, item): boolean {
    return item.isGroupBy;
  }

  isNotGroup(index, item): boolean {
    return !item.hasOwnProperty('isGroupBy');
  }

  isHiddenParameter(parameter: string): boolean {
    return isHiddenParameter(
      parameter,
      this.userParameters(),
      this.resolution(),
      this.parameterDefinitions()
    );
  }

  isParameterAvailableForResolution(parameter: string): boolean {
    return isParameterAvailableForResolution(
      parameter,
      this.resolution(),
      this.parameterDefinitions()
    );
  }

  isNight(dtg: string): boolean {
    return isNight(dtg);
  }

  listenToInputChanges() {
    effect(
      () => {
        this.tableData();
        this.parameterChanged();
        this.parameterset();
        this.userParameters();
        this.parameterDefinitions();
        this.resolution();
        this.dashboardView();
        this.stickyHeader();
        this.highlightedHour();

        untracked(() => {
          this.parameters.set(null);
          this.parameters.set([...this.extractParameters()]);
          this.setColumns();
        });
      },
      { injector: this.injector }
    );
  }
}
